import { EnvType } from "../services";
import { IApiClient } from "../api-client";
import { GetBroadcastersListResponse } from "../Types/get-broadcasters-list-response";
import { GetDeliveryProductsListResponse } from "../Types/get-delivery-products-list-response";
import { DbDeliveryProductInfos } from "../Types/db-delivery-product-infos";
import { GenericResponseStd } from "../Types/generic-response-std";
import { GetCustomersListResponse } from "../Types/get-customers-list-response";
import { DbCustomerInfos } from "../Types/db-customer-infos";
import { GetOrdersListResponse } from "../Types/get-orders-list-response";
import { GetOrderResponse } from "../Types/get-order-response";
import { GetOrderItemsForOrderIdResponse } from "../Types/get-order-items-for-order-id-response";
import { SetOrderFollowersRequest } from "../Types/set-order-followers-request";
import { GetItemsCountersResponse } from "../Types/get-items-counters-response";
import { GetItemsListResponse } from "../Types/get-items-list-response";
import { AclWorkflowAccessLevel } from "../Types/acl-workflow-access-level";
import { GetOrderItemResponse } from "../Types/get-order-item-response";
import { GetOrderItemStackResponse } from "../Types/get-order-item-stack-response";
import { GetOrderItemManualTasksResponse } from "../Types/get-order-item-manual-tasks-response";
import { ApplyManualChangesOnItemByIdRequest } from "../Types/apply-manual-changes-on-item-by-id-request";
import { ApplyManualChangesOnItemByIdsRequest } from "../Types/apply-manual-changes-on-item-by-ids-request";
import { ApplyManualChangesOnItemByIdsResponse } from "../Types/apply-manual-changes-on-item-by-ids-response";
import { SetOrderItemFollowersRequest } from "../Types/set-order-item-followers-request";
import { GetOrderItemIssueTypesResponse } from "../Types/get-order-item-issue-types-response";
import { GetOrderItemIssuesResponse } from "../Types/get-order-item-issues-response";
import { GetMissingAssetsByTitleId } from "../Types/get-missing-assets-by-title-id";
import { GetBenefitsTypesResponse } from "../Types/get-benefits-types-response";
import { CreateInternalOrderRequest } from "../Types/create-internal-order-request";
import { GetMetadataFieldsListResponse } from "../Types/get-metadata-fields-list-response";
import { GetCurrenciesResponse } from "../Types/get-currencies-response";
import { GetPriceRulesResponse } from "../Types/get-price-rules-response";
import { DbOrderPriceRuleInfos } from "../Types/db-order-price-rule-infos";
import { MatchPriceRuleRequest } from "../Types/match-price-rule-request";
import { ContentResult } from "../Types/content-result";
import { GetCustomerResponse } from "../Types/get-customer-response";
import { AxiosResponseHeaders } from "axios";
import { CreateQuoteOrderRequest } from "../Types/create-quote-order-request";
import { GetWatermarksResponse } from "../Types/get-watermarks-response";
import { GetWatermarkResponse } from "../Types/get-watermark-response";
import { ResendTransferEmailRequest } from "../Types/resend-transfer-email-request";
import { CreateSimpleOrderRequest } from "../Types/create-simple-order-request";

interface IOrderApiClient {
  GetBroadcastersList(): Promise<GetBroadcastersListResponse>;
  DownloadBroadcasterDocuments(broadcasterId: number): Promise<[string, AxiosResponseHeaders]>;
  GetDeliveryProductsList(): Promise<GetDeliveryProductsListResponse>;
  GetAllDeliveryProductsList(): Promise<GetDeliveryProductsListResponse>;
  UpdateProduct(request: DbDeliveryProductInfos): Promise<GenericResponseStd>;
  GetCustomersList(): Promise<GetCustomersListResponse>;
  GetCustomerById(id: number): Promise<GetCustomerResponse>;
  CreateCustomer(request: DbCustomerInfos): Promise<GenericResponseStd>;
  EditCustomer(request: DbCustomerInfos): Promise<GenericResponseStd>;
  DeleteCustomer(id: number): Promise<GenericResponseStd>;
  GetCustomersByConnectedAccount(): Promise<GetCustomersListResponse>;
  SyncCustomerByErp(): Promise<GenericResponseStd>;
  GetOrdersList(pageNb: number, pageSize: number): Promise<GetOrdersListResponse>;
  GetOrdersListByDate(startDateAsString: string, endDateAsString: string): Promise<GetOrdersListResponse>;
  GetOrderById(orderId: number): Promise<GetOrderResponse>;
  PlayOrderById(orderId: number): Promise<GenericResponseStd>;
  CancelOrderById(orderId: number): Promise<GenericResponseStd>;
  GetOrderItemsForOrderId(orderId: number, accessKey: string): Promise<GetOrderItemsForOrderIdResponse>;
  SetOrderFollowers(request: SetOrderFollowersRequest): Promise<GenericResponseStd>;
  DownloadOrdersItemsListAsCSV(startDateAsString: string, endDateAsString: string): Promise<GenericResponseStd>;
  GetItemsCounters(): Promise<GetItemsCountersResponse>;
  GetItemsList(pageNb: number, pageSize: number): Promise<GetItemsListResponse>;
  GetItemsListByWorkflowAccessLevel(
    pageNb: number,
    pageSize: number,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<GetItemsListResponse>;
  GetItemsListByDate(startDateAsString: string, endDateAsString: string): Promise<GetItemsListResponse>;
  GetItemsListQuoteByDate(startDateAsString: string, endDateAsString: string): Promise<GetItemsListResponse>;
  GetItemsListByDateAndWorkflowAccessLevel(
    startDateAsString: string,
    endDateAsString: string,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<GetItemsListResponse>;
  GetOrdersItemsListByTitleId(titleId: number, pageNb: number, pageSize: number): Promise<GetItemsListResponse>;
  GetOrderItem(itemId: number): Promise<GetOrderItemResponse>;
  GetOrderItemStack(itemId: number): Promise<GetOrderItemStackResponse>;
  GetOrderItemManualTasks(itemId: number): Promise<GetOrderItemManualTasksResponse>;
  PlayOrderItemById(itemId: number): Promise<GenericResponseStd>;
  SendInvoiceAck(itemId: number): Promise<GenericResponseStd>;
  CancelOrderItemById(itemId: number): Promise<GenericResponseStd>;
  RetryOrderItemById(itemId: number): Promise<GenericResponseStd>;
  RetryFromScratchOrderItemById(itemId: number): Promise<GenericResponseStd>;
  SetPriorityOnOrderItemById(itemId: number, priority: number): Promise<GenericResponseStd>;
  ApplyManualChangesOnItemById(request: ApplyManualChangesOnItemByIdRequest): Promise<GenericResponseStd>;
  ApplyManualChangesOnItemByIds(
    request: ApplyManualChangesOnItemByIdsRequest
  ): Promise<ApplyManualChangesOnItemByIdsResponse>;
  ModifyOrderItem(itemId: number): Promise<GenericResponseStd>;
  AssignColorToOrderItem(itemId: number, hexa: string): Promise<GenericResponseStd>;
  SetOrderItemFollowers(request: SetOrderItemFollowersRequest): Promise<GenericResponseStd>;
  RemoveIssueFromOrderItem(itemId: number, issueId: number): Promise<GenericResponseStd>;
  GetOrderItemIssueTypes(productType: string): Promise<GetOrderItemIssueTypesResponse>;
  GetIssuesByOrderItemId(itemId: number): Promise<GetOrderItemIssuesResponse>;
  RenderNotification(itemId: number, authKey: string): Promise<ContentResult>;
  GetMissingAssetsCandidatesByTitleId(titleId: number): Promise<GetMissingAssetsByTitleId>;
  GetBenefitsTypes(): Promise<GetBenefitsTypesResponse>;
  CreateInternalOrder(request: CreateInternalOrderRequest): Promise<GenericResponseStd>;
  CreateQuoteRequestOrder(request: CreateQuoteOrderRequest): Promise<GenericResponseStd>;
  GetMetadataFields(): Promise<GetMetadataFieldsListResponse>;
  GetCurrencies(): Promise<GetCurrenciesResponse>;
  GetPriceRules(): Promise<GetPriceRulesResponse>;
  CreatePriceRule(priceRule: DbOrderPriceRuleInfos): Promise<GenericResponseStd>;
  UpdatePriceRule(priceRule: DbOrderPriceRuleInfos): Promise<GenericResponseStd>;
  DeletePriceRule(id: number): Promise<GenericResponseStd>;
  MatchPriceRule(request: MatchPriceRuleRequest): Promise<GetPriceRulesResponse>;
  ResendTransferEmail(request: ResendTransferEmailRequest): Promise<GenericResponseStd>;
}
export default class OrderApiClient implements IOrderApiClient {
  readonly servicePort = 9506;
  readonly serviceBaseUrl = "order-service/api";

  orderApiClient: IApiClient;

  constructor(orderApiClient: IApiClient, envType: EnvType) {
    this.orderApiClient = orderApiClient;
    if (envType === "local") this.orderApiClient.client.defaults.baseURL += `${this.servicePort}/`;
  }

  async GetBroadcastersList(): Promise<GetBroadcastersListResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/broadcasters`);
  }

  async GetBroadcastersBundlemakerList(): Promise<GetBroadcastersListResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/broadcasters/bundles`);
  }

  async DownloadBroadcasterDocuments(broadcasterId: number): Promise<[string, AxiosResponseHeaders]> {
    return await this.orderApiClient.getWithHeaders(
      `${this.serviceBaseUrl}/broadcaster/documents/${broadcasterId}`,
      "arraybuffer"
    );
  }
  async GetDeliveryProductsList(): Promise<GetDeliveryProductsListResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/products`);
  }
  async GetAllDeliveryProductsList(): Promise<GetDeliveryProductsListResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/products-all`);
  }
  async UpdateProduct(request: DbDeliveryProductInfos): Promise<GenericResponseStd> {
    return await this.orderApiClient.put(`${this.serviceBaseUrl}/products`, request);
  }
  async GetCustomersList(): Promise<GetCustomersListResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/customers`);
  }
  async GetCustomerById(id: number): Promise<GetCustomerResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/customers/${id}`);
  }
  async CreateCustomer(request: DbCustomerInfos): Promise<GenericResponseStd> {
    return await this.orderApiClient.post(`${this.serviceBaseUrl}/customers`, request);
  }
  async EditCustomer(request: DbCustomerInfos): Promise<GenericResponseStd> {
    return await this.orderApiClient.put(`${this.serviceBaseUrl}/customers`, request);
  }
  async DeleteCustomer(id: number): Promise<GenericResponseStd> {
    return await this.orderApiClient.delete(`${this.serviceBaseUrl}/customers/${id}`);
  }
  async GetCustomersByConnectedAccount(): Promise<GetCustomersListResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/customers-account-links`);
  }
  async SyncCustomerByErp(): Promise<GenericResponseStd> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/customers/sync/erp`);
  }
  async GetOrdersList(pageNb: number, pageSize: number): Promise<GetOrdersListResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/orders/list/${pageNb}/${pageSize}`);
  }
  async GetOrdersListByDate(startDateAsString: string, endDateAsString: string): Promise<GetOrdersListResponse> {
    return await this.orderApiClient.get(
      `${this.serviceBaseUrl}/orders/list/dates/${startDateAsString}/${endDateAsString}`
    );
  }
  async GetOrderById(orderId: number): Promise<GetOrderResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/orders/${orderId}`);
  }
  async PlayOrderById(orderId: number): Promise<GenericResponseStd> {
    return await this.orderApiClient.put(`${this.serviceBaseUrl}/orders/${orderId}`);
  }
  async CancelOrderById(orderId: number): Promise<GenericResponseStd> {
    return await this.orderApiClient.delete(`${this.serviceBaseUrl}/orders/${orderId}`);
  }
  async GetOrderItemsForOrderId(orderId: number, accessKey: string): Promise<GetOrderItemsForOrderIdResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/orders/monitor/followup/${orderId}/${accessKey}`);
  }
  async SetOrderFollowers(request: SetOrderFollowersRequest): Promise<GenericResponseStd> {
    return await this.orderApiClient.post(`${this.serviceBaseUrl}/orders/followers`, request);
  }
  async GetOrdersByCustomerId(customerId: number): Promise<GetOrderItemsForOrderIdResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/orders/customer/${customerId}`);
  }
  async DownloadOrdersItemsListAsCSV(startDateAsString: string, endDateAsString: string): Promise<GenericResponseStd> {
    return await this.orderApiClient.get(
      `${this.serviceBaseUrl}/orders/items/list/dates/${startDateAsString}/${endDateAsString}/csv`
    );
  }
  async GetItemsCounters(): Promise<GetItemsCountersResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/orders/items/counters`);
  }
  async GetItemsList(pageNb: number, pageSize: number): Promise<GetItemsListResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/orders/items/list/${pageNb}/${pageSize}`);
  }
  async GetItemsListByWorkflowAccessLevel(
    pageNb: number,
    pageSize: number,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<GetItemsListResponse> {
    return await this.orderApiClient.get(
      `${this.serviceBaseUrl}/orders/items/list/${pageNb}/${pageSize}/${workflowAccessLevel}`
    );
  }
  async GetItemsListByDate(startDateAsString: string, endDateAsString: string): Promise<GetItemsListResponse> {
    return await this.orderApiClient.get(
      `${this.serviceBaseUrl}/orders/items/list/dates/${startDateAsString}/${endDateAsString}`
    );
  }
  async GetItemsListQuoteByDate(startDateAsString: string, endDateAsString: string): Promise<GetItemsListResponse> {
    return await this.orderApiClient.get(
      `${this.serviceBaseUrl}/orders/items/list/dates/${startDateAsString}/${endDateAsString}/quotes`
    );
  }
  async GetItemsListByDateAndWorkflowAccessLevel(
    startDateAsString: string,
    endDateAsString: string,
    workflowAccessLevel: AclWorkflowAccessLevel
  ): Promise<GetItemsListResponse> {
    return await this.orderApiClient.get(
      `${this.serviceBaseUrl}/orders/items/list/dates/${startDateAsString}/${endDateAsString}/${workflowAccessLevel}`
    );
  }
  async GetOrdersItemsListByTitleId(titleId: number, pageNb: number, pageSize: number): Promise<GetItemsListResponse> {
    return await this.orderApiClient.get(
      `${this.serviceBaseUrl}/orders/items/list/titles/${titleId}/${pageNb}/${pageSize}`
    );
  }
  async GetOrdersItemsListByAssetId(assetId: number, pageNb: number, pageSize: number): Promise<GetItemsListResponse> {
    return await this.orderApiClient.get(
      `${this.serviceBaseUrl}/orders/items/list/assets/${assetId}/${pageNb}/${pageSize}`
    );
  }
  async GetOrderItem(itemId: number): Promise<GetOrderItemResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/orders/items/${itemId}`);
  }
  async GetOrderItemStack(itemId: number): Promise<GetOrderItemStackResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/orders/items/${itemId}/stack`);
  }
  async GetOrderItemManualTasks(itemId: number): Promise<GetOrderItemManualTasksResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/orders/items/${itemId}/manual-tasks`);
  }
  async PlayOrderItemById(itemId: number): Promise<GenericResponseStd> {
    return await this.orderApiClient.post(`${this.serviceBaseUrl}/orders/items/${itemId}`);
  }
  async SendInvoiceAck(itemId: number): Promise<GenericResponseStd> {
    return await this.orderApiClient.post(`${this.serviceBaseUrl}/orders/items/${itemId}/invoice-ack`);
  }
  async CancelOrderItemById(itemId: number): Promise<GenericResponseStd> {
    return await this.orderApiClient.delete(`${this.serviceBaseUrl}/orders/items/${itemId}`);
  }
  async RetryOrderItemById(itemId: number): Promise<GenericResponseStd> {
    return await this.orderApiClient.put(`${this.serviceBaseUrl}/orders/items/${itemId}`);
  }
  async RetryFromScratchOrderItemById(itemId: number): Promise<GenericResponseStd> {
    return await this.orderApiClient.put(`${this.serviceBaseUrl}/orders/items/${itemId}/scratch`);
  }
  async SetPriorityOnOrderItemById(itemId: number, priority: number): Promise<GenericResponseStd> {
    return await this.orderApiClient.put(`${this.serviceBaseUrl}/orders/items/${itemId}/priority/${priority}`);
  }
  async ApplyManualChangesOnItemById(request: ApplyManualChangesOnItemByIdRequest): Promise<GenericResponseStd> {
    return await this.orderApiClient.post(`${this.serviceBaseUrl}/orders/items/manual-changes`, request);
  }
  async ApplyManualChangesOnItemByIds(
    request: ApplyManualChangesOnItemByIdsRequest
  ): Promise<ApplyManualChangesOnItemByIdsResponse> {
    return await this.orderApiClient.post(`${this.serviceBaseUrl}/orders/items/manual-changes-multiple`, request);
  }
  async ModifyOrderItem(itemId: number): Promise<GenericResponseStd> {
    return await this.orderApiClient.put(`${this.serviceBaseUrl}/orders/items/${itemId}/modify`);
  }
  async CopyOrderItemToBasket(itemId: number): Promise<GenericResponseStd> {
    return await this.orderApiClient.put(`${this.serviceBaseUrl}/orders/items/${itemId}/copy-to-basket`);
  }
  async AssignColorToOrderItem(itemId: number, hexa: string): Promise<GenericResponseStd> {
    return await this.orderApiClient.put(`${this.serviceBaseUrl}/orders/items/${itemId}/color/${hexa}`);
  }
  async SetOrderItemFollowers(request: SetOrderItemFollowersRequest): Promise<GenericResponseStd> {
    return await this.orderApiClient.post(`${this.serviceBaseUrl}/orders/items/followers`, request);
  }
  async RemoveIssueFromOrderItem(itemId: number, issueId: number): Promise<GenericResponseStd> {
    return await this.orderApiClient.delete(`${this.serviceBaseUrl}/orders/items/${itemId}/issues/${issueId}`);
  }
  async GetOrderItemIssueTypes(productType: string): Promise<GetOrderItemIssueTypesResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/orders/items/issues/types/${productType}`);
  }
  async GetIssuesByOrderItemId(itemId: number): Promise<GetOrderItemIssuesResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/orders/items/${itemId}/issues`);
  }
  async RenderNotification(itemId: number, authKey: string): Promise<ContentResult> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/orders/items/${itemId}/email/${authKey}`);
  }
  async GetMissingAssetsCandidatesByTitleId(titleId: number): Promise<GetMissingAssetsByTitleId> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/missing-assets/candidates/titles/${titleId}`);
  }
  async GetBenefitsTypes(): Promise<GetBenefitsTypesResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/benefit-types`);
  }
  async CreateInternalOrder(request: CreateInternalOrderRequest): Promise<GenericResponseStd> {
    return await this.orderApiClient.post(`${this.serviceBaseUrl}/internal-orders`, request);
  }
  async CreateQuoteRequestOrder(request: CreateQuoteOrderRequest): Promise<GenericResponseStd> {
    return await this.orderApiClient.post(`${this.serviceBaseUrl}/quote-orders`, request);
  }
  async CreateSimpleOrder(request: CreateSimpleOrderRequest): Promise<GenericResponseStd> {
    return await this.orderApiClient.post(`${this.serviceBaseUrl}/simple-orders`, request);
  }
  async GetMetadataFields(): Promise<GetMetadataFieldsListResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/items/metadata/fields`);
  }
  async GetCurrencies(): Promise<GetCurrenciesResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/currency/enums`);
  }
  async GetPriceRules(): Promise<GetPriceRulesResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/price-rules`);
  }
  async CreatePriceRule(priceRule: DbOrderPriceRuleInfos): Promise<GenericResponseStd> {
    return await this.orderApiClient.post(`${this.serviceBaseUrl}/price-rules`, priceRule);
  }
  async UpdatePriceRule(priceRule: DbOrderPriceRuleInfos): Promise<GenericResponseStd> {
    return await this.orderApiClient.put(`${this.serviceBaseUrl}/price-rules`, priceRule);
  }
  async DeletePriceRule(id: number): Promise<GenericResponseStd> {
    return await this.orderApiClient.delete(`${this.serviceBaseUrl}/price-rules/${id}`);
  }
  async MatchPriceRule(request: MatchPriceRuleRequest): Promise<GetPriceRulesResponse> {
    return await this.orderApiClient.post(`${this.serviceBaseUrl}/price-rules/check`, request);
  }
  async GetWatermarks(pageNumber: number, pageSize: number): Promise<GetWatermarksResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/watermarks/${pageNumber}/${pageSize}`);
  }
  async GetWatermarksByDate(startDate: string, endDate: string): Promise<GetWatermarksResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/watermarks/${startDate}/${endDate}`);
  }
  async GetWatermarkByKey(key: number): Promise<GetWatermarkResponse> {
    return await this.orderApiClient.get(`${this.serviceBaseUrl}/watermarks/payload/${key}`);
  }
  async ResendTransferEmail(request: ResendTransferEmailRequest): Promise<GenericResponseStd> {
    return await this.orderApiClient.post(`${this.serviceBaseUrl}/orders/items/emails/transfer/resend`, request);
  }

  //------Utils------
  GetApiUrl(): string {
    return this.orderApiClient.client.defaults.baseURL + this.serviceBaseUrl;
  }
}
